import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { Icon } from "@iconify/react";
import { useClientApiKeys } from "../../hooks/useClientApiKeys";
import { getClientWorkflows, previewTrigger } from "../../services/workflowTriggers";
import {
  buildTypedConfig,
  formatDateTime,
  guessTimezone,
  isFieldVisible,
  isoToLocalInput,
  localInputToIso,
  timezoneOptions,
} from "../../utils/workflowTriggers";

const fieldStyles = `
  width: 100%;
  padding: 7px 9px;
  font-size: 13px;
  font-family: inherit;
  color: var(--main-font-color);
  background-color: var(--main-bg);
  border: 1px solid var(--main-border-color);
  border-radius: 6px;
  outline: none;
  box-sizing: border-box;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  width: 100%;

  color: var(--main-font-color);`;

const Label = styled.label`
  display: flex;
  align-items: baseline;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: var(--main-font-color);
`;

const Control = styled.div`
  width: 100%;

  color: var(--main-font-color);`;

const Input = styled.input`
  ${fieldStyles}

  color: var(--main-font-color);`;

const Textarea = styled.textarea`
  ${fieldStyles}
  resize: vertical;

  color: var(--main-font-color);`;

const Select = styled.select`
  ${fieldStyles}
  cursor: pointer;

  /* The dark theme sets "select, select option { color: black }", which out-specifies a single
     class — without !important the dropdown is black-on-dark and unreadable. */
  color: var(--main-font-color) !important;
  background-color: var(--main-bg) !important;

  option {
    color: var(--main-font-color) !important;
    background-color: var(--main-bg) !important;
  }
`;

const Hint = styled.div`
  font-size: 11.5px;
  font-style: italic;
  color: #a0a6b1;
`;

const FieldError = styled.div`
  font-size: 12px;
  color: #e74c3c;
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: 100%;

  color: var(--main-font-color);`;

const AdvancedToggle = styled.button`
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 6px;
  border: none;
  background: transparent;
  color: #a0a6b1;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;

  color: var(--main-font-color);`;

const CheckboxLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
  color: var(--main-font-color);
  cursor: pointer;
`;

const PreviewBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 12px;
  border: 1px solid var(--main-border-color);
  border-radius: 8px;
  background-color: var(--main-bg);

  color: var(--main-font-color);`;

const PreviewTitle = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #a0a6b1;
`;

const PreviewList = styled.ol`
  margin: 0;
  padding-left: 18px;
  font-size: 12.5px;
  color: var(--main-font-color);
`;

const Note = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
  padding: 9px 11px;
  font-size: 12.5px;
  line-height: 1.5;
  border: 1px solid ${(props) => props.$tone ?? "var(--main-border-color)"};
  border-radius: 8px;
  background-color: var(--main-bg);
  color: var(--main-font-color);
`;

const jsonText = (value) => {
  if (value === undefined || value === null || value === "") return "";
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch (error) {
    return String(value);
  }
};

const toggleInList = (list, item) => {
  const current = Array.isArray(list) ? list : [];
  return current.includes(item) ? current.filter((entry) => entry !== item) : [...current, item];
};

/** Initial form values: the draft's config, then catalog defaults, then a few sensible fallbacks. */
const initialValues = (fields, draft) => {
  const values = { ...(draft?.config ?? {}) };

  fields.forEach((field) => {
    const name = field?.name;
    if (!name || values[name] !== undefined) return;
    if (field.default !== undefined) values[name] = field.default;
  });

  // A schedule without a timezone defaults to the browser's, which is what users expect.
  const timezoneField = fields.find((field) => field.name === "timezone");
  if (timezoneField && !values.timezone) values.timezone = guessTimezone();

  return values;
};

/**
 * Create/edit one trigger, rendered from the server's trigger catalog.
 */
export const TriggerEditor = ({
  typeMeta,
  draft = null,
  clientPublicId = null,
  workflowPublicId = null,
  saving = false,
  error = null,
  fieldErrors = {},
  onCancel,
  onSubmit,
}) => {
  const fields = useMemo(() => typeMeta?.fields ?? [], [typeMeta]);

  const [values, setValues] = useState(() => initialValues(fields, draft));
  const [name, setName] = useState(draft?.name ?? "");
  const [description, setDescription] = useState(draft?.description ?? "");
  const [enabled, setEnabled] = useState(draft?.enabled !== false);
  const [apiKeyName, setApiKeyName] = useState(draft?.api_key_name ?? "");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [localErrors, setLocalErrors] = useState({});

  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  const [workflows, setWorkflows] = useState([]);
  const [workflowsError, setWorkflowsError] = useState(null);

  const { keys: apiKeys, loading: apiKeysLoading } = useClientApiKeys(
    clientPublicId ? { clientId: clientPublicId } : {}
  );

  const visibleFields = fields.filter((field) => isFieldVisible(field, values));
  const basicFields = visibleFields.filter((field) => !field.advanced);
  const advancedFields = visibleFields.filter((field) => field.advanced);

  const hasMultipicker = fields.some((field) => String(field.control).toLowerCase() === "workflow-multipicker");

  useEffect(() => {
    if (!hasMultipicker || !clientPublicId) return undefined;

    let cancelled = false;

    getClientWorkflows(clientPublicId)
      .then((result) => {
        if (cancelled) return;
        if (result?.success === false) {
          setWorkflowsError(result.message || "Could not load the client's workflows.");
          setWorkflows([]);
          return;
        }
        setWorkflows(result.workflows ?? []);
        setWorkflowsError(null);
      })
      .catch((loadError) => {
        if (!cancelled) setWorkflowsError(loadError?.message || "Could not load the client's workflows.");
      });

    return () => {
      cancelled = true;
    };
  }, [hasMultipicker, clientPublicId]);

  // Debounced schedule preview (the server owns cron/interval normalisation).
  useEffect(() => {
    if (typeMeta?.type !== "schedule") return undefined;

    const handle = window.setTimeout(async () => {
      const { config, errors } = buildTypedConfig(fields, values);
      if (Object.keys(errors).length > 0) return;

      // Nothing to schedule yet — avoid a pointless (and noisy) 400 from the preview endpoint.
      const scheduleKeys = Object.keys(config).filter(
        (key) => !["mode", "timezone", "catch_up"].includes(key)
      );
      if (scheduleKeys.length === 0) {
        setPreview(null);
        setPreviewError(null);
        return;
      }

      setPreviewing(true);

      const result = await previewTrigger({ type: "schedule", config, count: 5 });

      setPreviewing(false);

      if (result?.success) {
        setPreview({ normalized: result.normalized_config ?? {}, next: result.next_runs ?? [] });
        setPreviewError(null);
      } else {
        setPreview(null);
        setPreviewError(result?.message || "Could not preview this schedule.");
      }
    }, 400);

    return () => window.clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, typeMeta?.type]);

  const setValue = (fieldName, value) => setValues((current) => ({ ...current, [fieldName]: value }));

  const submit = () => {
    const errors = {};

    fields.forEach((field) => {
      if (!field?.required) return;
      if (!isFieldVisible(field, values)) return;
      const value = values[field.name];
      if (value === undefined || value === null || value === "") {
        errors[field.name] = `${field.label ?? field.name} is required.`;
      }
    });

    if (typeMeta?.requires_api_key && !apiKeyName) {
      errors.api_key_name = "Choose a stored API key.";
    }

    const { config, errors: configErrors } = buildTypedConfig(fields, values);
    Object.assign(errors, configErrors);

    if (Object.keys(errors).length > 0) {
      setLocalErrors(errors);
      return;
    }

    setLocalErrors({});

    // Prefer the server's normalised values (e.g. "1h" → interval_seconds).
    const merged = { ...config };
    if (preview?.normalized) {
      fields.forEach((field) => {
        if (preview.normalized[field.name] !== undefined) merged[field.name] = preview.normalized[field.name];
      });
    }

    onSubmit({
      ...(draft?.public_id ? { public_id: draft.public_id } : {}),
      type: typeMeta.type,
      name: name.trim() || typeMeta.label || typeMeta.type,
      description,
      enabled,
      ...(apiKeyName ? { api_key_name: apiKeyName } : {}),
      config: merged,
    });
  };

  const errorFor = (fieldName) => localErrors[fieldName] ?? fieldErrors[fieldName] ?? null;

  const controlFor = (field) => {
    const control = String(field?.control ?? "text").toLowerCase();
    const value = values[field?.name];

    switch (control) {
      case "textarea":
        return (
          <Textarea
            rows={3}
            value={value ?? ""}
            onChange={(event) => setValue(field.name, event.target.value)}
            placeholder={field.placeholder ?? ""}
          />
        );

      case "number":
        return (
          <Input
            type="number"
            value={value ?? ""}
            onChange={(event) => setValue(field.name, event.target.value === "" ? undefined : Number(event.target.value))}
            placeholder={field.placeholder ?? ""}
          />
        );

      case "switch":
        return (
          <CheckboxLabel>
            <input
              type="checkbox"
              checked={Boolean(value)}
              onChange={(event) => setValue(field.name, event.target.checked)}
            />
            {Boolean(value) ? "on" : "off"}
          </CheckboxLabel>
        );

      case "select":
        return (
          <Select value={value ?? ""} onChange={(event) => setValue(field.name, event.target.value)}>
            <option value="">—</option>
            {(field.options ?? []).map((option) => (
              <option key={String(option.value)} value={option.value}>{option.label ?? option.value}</option>
            ))}
          </Select>
        );

      case "multiselect": {
        const selected = Array.isArray(value) ? value : [];
        return (
          <CheckboxGroup>
            {(field.options ?? []).map((option) => (
              <CheckboxLabel key={String(option.value)}>
                <input
                  type="checkbox"
                  checked={selected.includes(option.value)}
                  onChange={() => setValue(field.name, toggleInList(selected, option.value))}
                />
                {option.label ?? option.value}
              </CheckboxLabel>
            ))}
          </CheckboxGroup>
        );
      }

      case "cron":
        return (
          <>
            <Input
              value={value ?? ""}
              onChange={(event) => setValue(field.name, event.target.value)}
              placeholder="0 * * * *"
            />
            <Hint>Cron syntax · minute hour day-of-month month day-of-week</Hint>
          </>
        );

      case "datetime":
        return (
          <Input
            type="datetime-local"
            value={isoToLocalInput(value)}
            onChange={(event) => setValue(field.name, localInputToIso(event.target.value))}
          />
        );

      case "timezone": {
        const options = timezoneOptions();
        const withCurrent =
          value && !options.includes(value) ? [value, ...options] : options;

        return (
          <Select value={value ?? ""} onChange={(event) => setValue(field.name, event.target.value)}>
            {withCurrent.map((zone) => (
              <option key={zone} value={zone}>{zone}</option>
            ))}
          </Select>
        );
      }

      case "json":
      case "json-object":
        return (
          <Textarea
            rows={4}
            spellCheck={false}
            style={{ fontFamily: "monospace", fontSize: "12px" }}
            value={jsonText(value)}
            onChange={(event) => setValue(field.name, event.target.value)}
            placeholder={control === "json-object" ? "{}" : "{} / [] / value"}
          />
        );

      case "workflow-multipicker": {
        const selected = Array.isArray(value) ? value : [];
        const options = workflows.filter((workflow) => workflow.public_id !== workflowPublicId);

        if (!clientPublicId) return <Hint>This workflow has no client scope.</Hint>;
        if (workflowsError) return <FieldError>{workflowsError}</FieldError>;

        return (
          <CheckboxGroup>
            {options.length === 0 ? <Hint>No other workflows in this client.</Hint> : null}
            {options.map((workflow) => (
              <CheckboxLabel key={workflow.public_id}>
                <input
                  type="checkbox"
                  checked={selected.includes(workflow.public_id)}
                  onChange={() => setValue(field.name, toggleInList(selected, workflow.public_id))}
                />
                {workflow.name || workflow.public_id}
              </CheckboxLabel>
            ))}
          </CheckboxGroup>
        );
      }

      default:
        return (
          <Input
            value={value ?? ""}
            onChange={(event) => setValue(field.name, event.target.value)}
            placeholder={field.placeholder ?? ""}
          />
        );
    }
  };

  const renderField = (field) => (
    <Field key={field.name}>
      <Label>
        {field.label ?? field.name}
        {field.required ? <span style={{ color: "#e74c3c" }}>*</span> : null}
      </Label>
      <Control>{controlFor(field)}</Control>
      {field.description ? <Hint>{field.description}</Hint> : null}
      {errorFor(field.name) ? <FieldError>{errorFor(field.name)}</FieldError> : null}
    </Field>
  );

  return (
    <Section>
      <Field>
        <Label>Name</Label>
        <Input value={name} onChange={(event) => setName(event.target.value)} placeholder={typeMeta?.label ?? ""} />
        {errorFor("name") ? <FieldError>{errorFor("name")}</FieldError> : null}
      </Field>

      <Field>
        <Label>Description</Label>
        <Input
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          placeholder="Optional"
        />
      </Field>

      <CheckboxLabel>
        <input type="checkbox" checked={enabled} onChange={(event) => setEnabled(event.target.checked)} />
        Enabled
      </CheckboxLabel>

      {typeMeta?.requires_api_key ? (
        <Field>
          <Label>API key</Label>
          <Select value={apiKeyName} onChange={(event) => setApiKeyName(event.target.value)}>
            <option value="">{apiKeysLoading ? "Loading keys…" : "Select a stored key"}</option>
            {apiKeys.map((key) => (
              <option key={key.name} value={key.name}>
                {key.name}{key.service ? ` · ${key.service}` : ""}{key.maskedValue ? ` (${key.maskedValue})` : ""}
              </option>
            ))}
          </Select>
          <Hint>
            Only the key name is stored. Secrets live in the client API keys settings.
            {typeMeta.type === "webhook" ? " The key value is used as the HMAC secret." : ""}
          </Hint>
          {errorFor("api_key_name") ? <FieldError>{errorFor("api_key_name")}</FieldError> : null}
        </Field>
      ) : null}

      {basicFields.map(renderField)}

      {advancedFields.length > 0 ? (
        <>
          <AdvancedToggle type="button" onClick={() => setShowAdvanced((open) => !open)}>
            <Icon icon={showAdvanced ? "lucide:chevron-down" : "lucide:chevron-right"} />
            Advanced ({advancedFields.length})
          </AdvancedToggle>
          {showAdvanced ? advancedFields.map(renderField) : null}
        </>
      ) : null}

      {typeMeta?.type === "schedule" ? (
        <PreviewBox>
          <PreviewTitle>Next 5 runs{previewing ? " · updating…" : ""}</PreviewTitle>
          {previewError ? <FieldError>{previewError}</FieldError> : null}
          {preview?.next?.length ? (
            <PreviewList>
              {preview.next.map((run) => (
                <li key={run}>{formatDateTime(run)}</li>
              ))}
            </PreviewList>
          ) : (
            <Hint>Add an interval, cron expression, or run-at time to see the schedule.</Hint>
          )}
          {preview?.normalized ? (
            <Hint>
              Server values:{" "}
              {Object.entries(preview.normalized)
                .map(([key, value]) => `${key}=${typeof value === "object" ? JSON.stringify(value) : value}`)
                .join(" · ")}
            </Hint>
          ) : null}
        </PreviewBox>
      ) : null}

      {typeMeta?.type === "api" ? (
        <Note>
          <Icon icon="lucide:info" />
          <span>
            The endpoint path is generated when you save. Callers send <code>X-API-Key: &lt;key name&gt;</code>;
            async runs return a run id you can poll.
          </span>
        </Note>
      ) : null}

      {typeMeta?.type === "webhook" ? (
        <Note>
          <Icon icon="lucide:info" />
          <span>
            The key value is the HMAC secret. Sign the <strong>raw</strong> body with
            <code> hex(HMAC_SHA256(secret, rawBody))</code> and send it in the configured signature header.
          </span>
        </Note>
      ) : null}

      {typeMeta?.type === "subworkflow" ? (
        <Note>
          <Icon icon="lucide:info" />
          <span>
            Add a Workflow node in the caller and set its workflow id to this workflow's public id
            (<code>{workflowPublicId}</code>). Empty selection = any workflow.
          </span>
        </Note>
      ) : null}

      {error ? <FieldError>{error}</FieldError> : null}

      <Field>
        <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: "7px 14px",
              fontSize: "13px",
              border: "1px solid var(--main-border-color)",
              borderRadius: "6px",
              background: "transparent",
              color: "var(--main-font-color)",
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            style={{
              padding: "7px 16px",
              fontSize: "13px",
              fontWeight: 600,
              border: "none",
              borderRadius: "6px",
              background: "var(--button-bg)",
              color: "var(--button-font-color)",
              cursor: saving ? "default" : "pointer",
              opacity: saving ? 0.6 : 1,
            }}
          >
            {saving ? "Saving…" : "Save trigger"}
          </button>
        </div>
      </Field>
    </Section>
  );
};

export default TriggerEditor;
